import * as React from 'react';
import { SignupStep1Controller, SignupStep1ControllerProps } from './SignupStep1.controller';
import { gql } from '@apollo/client';
import { useRegisterEmailAvailableLazyQuery, useStartSocialOAuthMutation } from '../../../models/graphql';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { Dispatch, SetStateAction } from 'react';
import { getApolloError } from '../../../constants/apollo-errors';

gql`
  query RegisterEmailAvailable($email: String!) {
    registerEmailAvailable(email: $email)
  }

  mutation StartSocialOAuth(
    $oAuthProvider: SocialOAuthProvider!
    $profileNetwork: Network
    $context: SocialOAuthContext!
  ) {
    startSocialOAuth(oAuthProvider: $oAuthProvider, profileNetwork: $profileNetwork, context: $context) {
      socialAuthToken
      redirectUrl
    }
  }
`;

export type SignupStep1ApolloProps = Omit<
  SignupStep1ControllerProps,
  | 'startSocialOAuth'
  | 'startSocialOAuthData'
  | 'registerEmailAvailable'
  | 'registerEmailAvailableEmail'
  | 'registerEmailAvailableResult'
  | 'signupEmail'
> & {
  //
  setEmailForRegistration: Dispatch<SetStateAction<string>>;
};

export const SignupStep1Apollo = (props: SignupStep1ApolloProps): JSX.Element => {
  const [
    startSocialOAuth,
    { loading: startSocialOAuthLoading, error: startSocialOAuthError, data: startSocialOAuthData }
  ] = useStartSocialOAuthMutation({
    fetchPolicy: 'no-cache'
  });
  const [
    registerEmailAvailable,
    {
      loading: registerEmailAvailableLoading,
      error: registerEmailAvailableError,
      data: registerEmailAvailableData,
      variables: registerEmailAvailableVariables
    }
  ] = useRegisterEmailAvailableLazyQuery({
    fetchPolicy: 'no-cache'
  });
  let submitted = props.submitted;
  let signupError = props.signupError;
  let signupEmail = props.invitation?.invitationEmail;

  //// Loading states
  if (startSocialOAuthLoading || registerEmailAvailableLoading) {
    submitted = true;
    signupError = '';
  }

  //// SocialOAuth
  if (startSocialOAuthError) {
    signupError = remapApolloErrors(startSocialOAuthError);
    submitted = false;
  }

  //// registerEmail Available
  if (registerEmailAvailableError) {
    submitted = false;
    signupError = remapApolloErrors(registerEmailAvailableError);
  }

  if (startSocialOAuthData) {
    submitted = true;
    signupError = '';
  }

  // intentionally check against false, since we want to be sure that response === false and not undefined due to server error or something similar, and use else to reset error since we might have error from social signup
  if (registerEmailAvailableData && registerEmailAvailableData.registerEmailAvailable === false) {
    signupError = getApolloError('SIGNUP_EMAIL_ALREADY_REGISTERED', 'registerEmailAvailable');
    submitted = false;
  } else if (registerEmailAvailableData?.registerEmailAvailable) {
    // set signupEmail to new value
    signupEmail = registerEmailAvailableVariables?.email;
  }

  const controllerProps: SignupStep1ControllerProps = {
    ...props,
    submitted,
    startSocialOAuth,
    startSocialOAuthData: startSocialOAuthData?.startSocialOAuth,
    signupEmail,
    registerEmailAvailableResult: registerEmailAvailableData?.registerEmailAvailable,
    registerEmailAvailable,
    signupError
  };
  return <SignupStep1Controller {...controllerProps} />;
};
